/**
 * Headline numbers for the Organizations and Outlets pages, worked out from
 * the lists the pages already load (organizations, outlets, schedules).
 */
const fmt = (value) => Number(value || 0).toLocaleString();
const plural = (count, one, many = `${one}s`) => `${fmt(count)} ${count === 1 ? one : many}`;
const placeKey = (value) => String(value || '').trim().toLowerCase();

function distinct(list, pick) {
  return new Set(list.map(pick).filter(Boolean)).size;
}

/** "4 states", or '' when no outlet has a state filled in. */
function statesText(outlets) {
  const states = distinct(outlets, (o) => placeKey(o.state));
  return states ? plural(states, 'state') : '';
}

const DAY = 24 * 60 * 60 * 1000;

function scheduleFacts(schedules, outletIds) {
  const inScope = outletIds ? schedules.filter((s) => outletIds.has(String(s.outlet_id))) : schedules;
  const now = Date.now();
  const live = inScope.filter((s) => s.schedule_status === 'in_progress');
  const closed = inScope.filter((s) => s.schedule_status === 'closed');
  const upcoming = inScope.filter((s) => s.schedule_status === 'not_started');
  const nextWeek = upcoming.filter((s) => new Date(s.start_at).getTime() - now <= 7 * DAY);
  return {
    total: inScope.length,
    live,
    closed,
    upcoming,
    nextWeek,
    liveStores: distinct(live, (s) => s.outlet_id && String(s.outlet_id)),
    liveBrands: distinct(live, (s) => s.organization_id && String(s.organization_id)),
  };
}

export function organizationKpis(organizations, outlets, schedules) {
  const orgsWithStores = new Set(outlets.map((o) => String(o.organization_id)));
  const withStores = organizations.filter((org) => orgsWithStores.has(String(org.id))).length;
  const facts = scheduleFacts(schedules);
  return [
    {
      label: 'Total associated clients',
      icon: 'building',
      tone: 'teal',
      value: plural(organizations.length, 'Brand'),
      note: `${fmt(withStores)} with stores · ${fmt(organizations.length - withStores)} without`,
    },
    {
      label: 'Total registered stores',
      icon: 'store',
      tone: 'info',
      value: plural(outlets.length, 'Store'),
      note: `Mapped across ${plural(distinct(outlets, (o) => placeKey(o.city)), 'city', 'cities')}`,
      extra: statesText(outlets),
    },
    {
      label: 'Schedules covered so far',
      icon: 'calendarCheck',
      tone: 'good',
      valueTone: facts.closed.length ? 'good' : '',
      value: plural(facts.closed.length, 'Audit'),
      note: `${fmt(facts.upcoming.length)} upcoming · ${fmt(facts.total)} scheduled in all`,
      extra: 'Completed (closed) schedules',
    },
    {
      label: 'Active ongoing audits today',
      icon: 'signal',
      tone: 'warn',
      valueTone: facts.liveStores ? 'warn' : 'muted',
      value: facts.liveStores ? `${plural(facts.liveStores, 'Store')} Live` : 'None live',
      note: facts.live.length
        ? `${plural(facts.live.length, 'schedule')} in progress · ${plural(facts.liveBrands, 'brand')}`
        : 'No schedule is in progress right now',
      extra: facts.nextWeek.length ? `${plural(facts.nextWeek.length, 'schedule')} starting in the next 7 days` : '',
    },
  ];
}

export function outletKpis(outlets, schedules, orgName) {
  const ids = new Set(outlets.map((o) => String(o.id)));
  const facts = scheduleFacts(schedules, ids);
  const audited = new Set(facts.closed.map((s) => String(s.outlet_id)));
  const scheduled = new Set(schedules.filter((s) => ids.has(String(s.outlet_id))).map((s) => String(s.outlet_id)));
  const neverScheduled = outlets.filter((o) => !scheduled.has(String(o.id))).length;
  const percent = outlets.length ? Math.round((audited.size / outlets.length) * 100) : 0;
  return [
    {
      label: orgName ? `${orgName} stores` : 'Total registered stores',
      icon: 'store',
      tone: 'info',
      value: plural(outlets.length, 'Store'),
      note: [`Across ${plural(distinct(outlets, (o) => placeKey(o.city)), 'city', 'cities')}`, statesText(outlets)].filter(Boolean).join(' · '),
    },
    {
      label: 'Stores audited',
      icon: 'calendarCheck',
      tone: 'good',
      valueTone: audited.size ? 'good' : '',
      value: `${fmt(audited.size)} / ${fmt(outlets.length)}`,
      note: `${percent}% have a completed schedule`,
      extra: `${plural(facts.closed.length, 'audit')} completed in all`,
    },
    {
      label: 'Not scheduled yet',
      icon: 'clock',
      tone: neverScheduled ? 'bad' : 'muted',
      valueTone: neverScheduled ? 'bad' : '',
      value: plural(neverScheduled, 'Store'),
      note: neverScheduled ? 'No schedule created for these stores' : 'Every store has a schedule',
      extra: facts.upcoming.length ? `${plural(facts.upcoming.length, 'upcoming schedule')}` : '',
    },
    {
      label: 'Live today',
      icon: 'signal',
      tone: 'warn',
      valueTone: facts.liveStores ? 'warn' : 'muted',
      value: facts.liveStores ? `${plural(facts.liveStores, 'Store')} Live` : 'None live',
      note: facts.liveStores ? 'Audit in progress (HHT scans syncing)' : 'No audit running right now',
      extra: facts.nextWeek.length ? `${plural(facts.nextWeek.length, 'schedule')} starting in the next 7 days` : '',
    },
  ];
}

// ---- Devices -----------------------------------------------------------------

/** Days from today to a YYYY-MM-DD date (negative = past). */
export function daysUntil(date) {
  if (!date) return null;
  const [y, m, d] = String(date).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const today = new Date();
  const start = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((Date.UTC(y, m - 1, d) - start) / DAY);
}

const money = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/** Headline numbers for the Devices page. */
export function deviceKpis(devices) {
  const count = (test) => devices.filter(test).length;
  const allowed = count((d) => d.is_active);
  const mobiles = count((d) => d.device_type === 'mobile' || !d.device_type);
  const hmc = count((d) => d.device_type === 'hmc');
  const others = devices.length - mobiles - hmc;
  const leased = devices.filter((d) => d.ownership === 'lease');
  const activeLeases = leased.filter((d) => d.condition_status !== 'retired' && !(daysUntil(d.lease_end) < 0));
  const rent = activeLeases.reduce((t, d) => t + Number(d.monthly_rent || 0), 0);
  const ending = leased.filter((d) => {
    const days = daysUntil(d.lease_end);
    return days !== null && days >= 0 && days <= 30;
  });
  const expired = leased.filter((d) => daysUntil(d.lease_end) < 0 && d.condition_status !== 'retired');
  const activeNow = count((d) => d.active_now);
  const used30 = count((d) => d.scans_30d > 0);
  const never = count((d) => !d.last_scan_at);
  const repair = count((d) => d.condition_status === 'repair');
  const retired = count((d) => d.condition_status === 'retired');
  return [
    {
      label: 'Registered devices',
      icon: 'phone',
      tone: 'info',
      value: plural(devices.length, 'Device'),
      note: `${fmt(allowed)} allowed · ${fmt(devices.length - allowed)} blocked`,
    },
    {
      label: 'Device mix',
      icon: 'barcode',
      tone: 'teal',
      value: `${fmt(mobiles)} Mobile · ${fmt(hmc)} HMC`,
      note: others ? `+ ${plural(others, 'tablet / other', 'tablets / other')}` : 'Phones and hand-held scanners',
    },
    {
      label: 'Owned vs leased',
      icon: 'building',
      tone: 'muted',
      value: `${fmt(devices.length - leased.length)} Own · ${fmt(leased.length)} Lease`,
      note: rent ? `${money(rent)} / month on ${plural(activeLeases.length, 'active lease')}` : `${plural(activeLeases.length, 'active lease')}`,
      extra: leased.length ? `${plural(new Set(leased.map((d) => (d.lease_vendor || '').trim().toLowerCase()).filter(Boolean)).size, 'vendor')}` : '',
    },
    {
      label: 'Leases ending in 30 days',
      icon: 'calendarCheck',
      tone: ending.length || expired.length ? 'warn' : 'good',
      valueTone: ending.length ? 'warn' : undefined,
      value: plural(ending.length, 'Device'),
      note: expired.length ? `${plural(expired.length, 'lease')} already expired — return or renew` : 'No expired leases in use',
    },
    {
      label: 'Scanning right now',
      icon: 'signal',
      tone: 'good',
      value: `${fmt(activeNow)} / ${fmt(allowed)}`,
      note: `${fmt(used30)} used in the last 30 days`,
      extra: never ? `${plural(never, 'device')} never synced a scan` : '',
    },
    {
      label: 'Under repair',
      icon: 'alert',
      tone: repair ? 'bad' : 'good',
      valueTone: repair ? 'bad' : undefined,
      value: plural(repair, 'Device'),
      note: `${fmt(retired)} retired · ${fmt(devices.length - repair - retired)} working`,
    },
  ];
}
